import { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
export type AccountTypeTab = "ACCOUNTS" | "DEPOSIT" | "LOANS" | "CARDS";
export type AccountsTab = "Assets" | "Liabilities";
import type { Account } from "@/app/types/accounts"; 

export interface FilterState {
  searchText: unknown;
  accountNo: string | null;
  cardNo: string | null;
  accountType: string | null;
  accountName: string | null;
  depositType: string | null;
  loanType: string | null;
  cardType: string | null;
  status: string | null;
  fromDate: string | null;
  toDate: string | null;
  minPrice: string | null;
  maxPrice: string | null;
  loanActivityType: string | null;
  cardActivityType: string | null;
  drCr: string | null;
  transactionType: string | null;
  transactionTypeId: string | null;
  transactionStatus: string | null;
  transactionStatusId: string | null;
  accountStatus: string | null;
  loanStatus: string | null;
  loanStatusId: string | null;
  cardStatus: string | null;
  cardStatusId: string | null;
  bank: string | null;
  bankId: string | null;
  accountStatusId: string | null;
  company: string | null;
  bankOptions: string[];
  other: string | null;
  date: string | null;
  pendingTransactionStatus: string | null;
  requestType: string | null;
  payeeName: string | null;
  frequency: string | null;
  paymentType: string | null;
  benStatus: string | null;
  benBank: string | null;
}

const initialFilters: FilterState = {
  accountNo: null,
  accountName: null,
  cardNo: null,
  accountType: null,
  depositType: null,
  loanType: null,
  cardType: null,
  status: null,
  fromDate: null,
  toDate: null,
  minPrice: null,
  maxPrice: null,
  loanActivityType: null,
  cardActivityType: null,
  drCr: null,
  transactionType: null,
  transactionStatus: null,
  transactionStatusId: null,
  accountStatus: null,
  loanStatus: null,
  loanStatusId: null,
  cardStatus: null,
  cardStatusId: null,
  bank: null,
  bankId: null,
  accountStatusId: null,
  company: null,
  bankOptions: [],
  other: null,
  date: null,
  pendingTransactionStatus: null,
  requestType: null,
  payeeName: null,
  frequency: null,
  paymentType: null,
  benStatus: null,
  benBank: null,
  searchText: null,
  transactionTypeId: null,
};

interface AccountsContextType {
  selectedAccount: Account | null;
  setSelectedAccount: React.Dispatch<React.SetStateAction<Account | null>>;
  activAccTypeTab: AccountTypeTab;
  setActivAccTypeTab: React.Dispatch<React.SetStateAction<AccountTypeTab>>;
  activeTab: AccountsTab;
  setActiveTab: React.Dispatch<React.SetStateAction<AccountsTab>>;
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  resetFilters: () => void;
  // logFilters: () => void;
  clearFilter: (key: keyof FilterState) => void;
  resetAllTabs: () => void;
  accountDetailsTab: {
    activeTab: number;
    setActiveTab: React.Dispatch<React.SetStateAction<number>>;
    resetActiveTab: () => void;
    activAccTypeTab: string;
    setActivAccTypeTab: React.Dispatch<React.SetStateAction<string>>;
    activePaymentsTab: string;
    setActivePaymentsTab: React.Dispatch<React.SetStateAction<string>>;
    activeTransferTab: string;
    setActiveTransferTab: React.Dispatch<React.SetStateAction<string>>;
  };
}

const AccountsContext = createContext<AccountsContextType | undefined>(
  undefined,
);

export function AccountsProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const [selectedAccount, setSelectedAccount] = useState<Account | null>(() => {  // ← change from unknown | null
  if (typeof window !== "undefined") {
    try {
      const stored = localStorage.getItem("selectedAccount");
      return stored ? JSON.parse(stored) as Account : null;  // ← add cast here
    } catch {
      return null;
    }
  }
  return null;
});

  const [activAccTypeTab, setActivAccTypeTab] = useState<AccountTypeTab>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("activAccTypeTab");
      if (
        stored &&
        ["ACCOUNTS", "DEPOSIT", "LOANS", "CARDS"].includes(stored)
      ) {
        return stored as AccountTypeTab;
      }
    }
    return "ACCOUNTS";
  });

  const [activeTab, setActiveTab] = useState<AccountsTab>(() => {
    if (typeof window !== "undefined") {
      if (window.location.pathname.startsWith("/accounts/liabilities")) {
        return "Liabilities";
      }
      if (window.location.pathname.startsWith("/accounts/assets")) {
        return "Assets";
      }
      const stored = localStorage.getItem("activeTab");
      if (stored && ["Assets", "Liabilities"].includes(stored)) {
        return stored as AccountsTab;
      }
    }
    return "Assets";
  });

  const [filters, setFilters] = useState<FilterState>(initialFilters);

  const [detailsActiveTab, setDetailsActiveTab] = useState<number>(1);
  const [detailsAccType, setDetailsAccType] = useState<string>("ACCOUNTS");
  const [activePaymentsTab, setActivePaymentsTab] =
    useState<string>("Quick Transfer");
  const [activeTransferTab, setActiveTransferTab] =
    useState<string>("Upcoming Transfers");

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const clearFilter = (key: keyof FilterState) => {
    setFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
  };

  const resetDetailsActiveTab = () => setDetailsActiveTab(1);

  const resetAllTabs = () => {
    setActivAccTypeTab("ACCOUNTS");
    setDetailsActiveTab(1);
    setDetailsAccType("ACCOUNTS");
    setActivePaymentsTab("Quick Transfer");
    setActiveTransferTab("Upcoming Transfers");

    if (typeof window !== "undefined") {
      localStorage.setItem("activeTab", "Assets");
      localStorage.setItem("activAccTypeTab", "ACCOUNTS");
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith("/accounts/liabilities")) {
      setActiveTab("Liabilities");
    } else if (location.pathname.startsWith("/accounts/assets")) {
      setActiveTab("Assets");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    localStorage.setItem("activAccTypeTab", activAccTypeTab);

    if (
      !location.pathname.startsWith("/accounts/assets") &&
      !location.pathname.startsWith("/accounts/liabilities")
    ) {
      localStorage.setItem("activeTab", activeTab);
    }
  }, [activAccTypeTab, activeTab, location]);

  /* --------------------------------------------------------
     🔍 DEBUG LOGS FOR ALL TABS
  --------------------------------------------------------- */

  // . Reset details tab AFTER routing (prevents UI flicker)
  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setDetailsActiveTab(1);
    }
  }, [location.pathname]);

  return (
    <AccountsContext.Provider
      value={{
        selectedAccount,
        setSelectedAccount,
        activAccTypeTab,
        setActivAccTypeTab,
        activeTab,
        setActiveTab,
        filters,
        setFilters,
        resetFilters,
        clearFilter,
        resetAllTabs,
        accountDetailsTab: {
          activeTab: detailsActiveTab,
          setActiveTab: setDetailsActiveTab,
          resetActiveTab: resetDetailsActiveTab,
          activAccTypeTab: detailsAccType,
          setActivAccTypeTab: setDetailsAccType,
          activePaymentsTab,
          setActivePaymentsTab,
          activeTransferTab,
          setActiveTransferTab,
        },
      }}
    >
      {children}
    </AccountsContext.Provider>
  );
}

export function useAccounts() {
  const ctx = useContext(AccountsContext);
  if (!ctx) throw new Error("useAccounts must be used inside AccountsProvider");
  return ctx;
}
